/**
 *  MapComponent script file
 **/
import * as d3 from 'd3'
import { formatNumber } from '@/utils/formatNumber'
import { clientMixin } from '../../mixins/fetch'

import Ranking from '@/components/ranking/Ranking.vue'

const omit = (keys, obj) => {
  const result = {}
  const index = keys.reduce((acc, cur, idx) => {
    acc[cur] = idx
    return acc
  }, {})

  Object.keys(obj).forEach((key) => {
    // eslint-disable-next-line no-prototype-builtins
    if (!index.hasOwnProperty(key)) result[key] = obj[key]
  })
  return result
}

/**
 * TODO:
 *
 * Load data correctly
 * Responsive
 * Improve tags navigation
 * Map tooltip
 * Select
 * Expand ranking
 */
export default {
  name: 'MapComponent',
  mixins: [clientMixin],
  components: {
    Ranking
  },
  props: {
    properties: {
      type: Object,
      required: true
    }
  },
  data () {
    const { config } = this.properties

    return {
      config: config,
      colors: config.colors,
      spinner: (config && config.colors && config.colors.spinner) || {},
      local: config.local,
      country: {
        id: 'all',
        name: config.country
      },
      remote: {
        id: 990,
        name: config && config.remote && config.remote.name,
        data: {
          total: 0
        }
      },
      jobTabs: [],
      published: config.published,
      text: config.text,
      date: config.date,
      flipMapX: config.flipMapX,
      flipMapY: config.flipMapY,
      legend: config.legend,
      toggleButtons: [
        {
          id: 'map',
          name: 'Mapa',
          color: '#ffffff',
          icon: {
            name: 'icon-map.svg',
            size: '20px',
            fill: '#ffffff' // TODO: as var
          },
          active: true
        },
        {
          id: 'ranking',
          name: 'Ranking',
          color: '#ffffff',
          icon: {
            name: 'icon-ranking.svg',
            size: '20px',
            fill: '#ffffff' // TODO: as var
          },
          active: false
        }
      ],
      formatNumber: formatNumber,
      values: [],
      items: [],
      total: 0,
      max: 0,
      median: 0,
      min: 0,
      barsData: [],
      selected: null,
      selectedTag: null,
      selectedColor: config.colors[config.mapActiveTag],
      visibleBars: 0,
      itemSelected: null,
      windowWidth: window.innerWidth,
      uid: null,
      tooltip: null,
      sourceAndDate: null,
      geo: {},
      selectData: [],
      features: []
    }
  },
  created () {
    this.uid = `component-${this._uid}`
  },
  async mounted () {
    const { mapActiveTag } = this.properties.config || {}

    await Promise.all([this.getProfessions(), this.getCountry()])

    this.selectedTag = this.tagButtons.find((tag) => tag.id === mapActiveTag)

    this.selectedColor = this.colors[mapActiveTag]
    this.currentData = this.data[mapActiveTag]

    this.visibleBars = this.windowWidth > 700 ? 10 : 3

    this.$nextTick(function () {
      if (!this.isLoading) {
        this.init()
        this.drawMap()
        this.resize()
      }
    })

    window.addEventListener('resize', this.resize, false)
    window.addEventListener('scroll', this.scroll, false)
  },
  computed: {

    hasRemote () {
      if (this.geo && this.geo.features) {
        return this.geo.features.some((item) => {
          return item.properties.id === this.remote.id
        })
      }

      return false
    },

    remoteConfig () {
      if (this.geo && this.geo.features) {
        const remoteProperty = this.geo.features.find((item) => {
          return item.properties.id === this.remote.id
        })

        return {
          ...remoteProperty.properties,
          ...(this.remote.name && {
            name: this.remote.name
          }),
          text:
            (this.properties &&
              this.properties.config &&
              this.properties.config.remote &&
              this.properties.config.remote.text) ||
            'Ofertas Remoto'
        }
      }

      return this.remote
    },

    remoteButton () {
      return this.$refs['btn-remote']
    },
    tagButtons () {
      return this.jobTabs.reduce((acum, current) => {
        const tag = {
          id: current.id,
          name: current.title,
          color: this.properties.config.colors[current.id],
          active: this.properties.config.mapActiveTag === current.id,
          spacer: this.properties.config.tagsSpacer.includes(current.id)
        }

        acum.push(tag)

        return acum
      }, [])
    },
    data () {
      return this.jobTabs.reduce((acum, current) => {
        acum[current.id] = current
        return acum
      }, {})
    },
    legendColorRamp () {
      return {
        'background-image': `linear-gradient(to left, ${this.properties.config.colors.range[2]}, ${this.properties.config.colors.range[1]} 50%, ${this.properties.config.colors.range[0]})`
      }
    }
  },
  methods: {
    getUid () {
      return this.uid
    },
    async getProfessions () {
      const { data } = this.properties

      if (!Array.isArray(data) || !data.length) {
        return
      }

      try {
        this.isLoading = true
        const data = await Promise.all(
          this.properties.data.map(async (key) => {
            try {
              const data = await this.getData(key)
              return data
            } catch (error) {
              return null
            }
          })
        )

        this.jobTabs = data.filter(Boolean)
      } catch (error) {
      } finally {
        this.isLoading = false
      }
    },
    async getCountry () {
      try {
        this.isLoadinGeoJson = true
        const data = await this.getGeoJson(
          `${this.config.mapFile || 'spain'}.geojson.json`
        )

        this.geo = data
      } catch (error) {
      } finally {
        this.isLoadinGeoJson = false
      }
    },
    init () {
      this.setValues()
      this.setFeatures()
      this.sortBarsData(this.values)
      this.setSelectedData()

      if (!this.itemSelected) {
        this.total = d3.sum(this.values, (d) => d.total)
      }
    },
    setValues () {
      if (this.currentData) {
        this.values = this.setTotalValues(this.currentData.values)
        this.sourceAndDate = this.currentData.date
      }
    },
    setFeatures () {
      this.features = this.geo.features.map((feature) => {
        const data = this.values.find(
          (value) => value.id === feature.properties.id
        )

        if (feature.properties.id === this.remote.id) {
          this.remote = {
            ...this.remote,
            data: { total: 0, ...data }
          }
        }

        return {
          ...feature,
          data: { total: 0, ...data }
        }
      })
    },
    setSelectedData () {
      this.selectData = this.features.map((feature) => {
        if (feature.properties.id === this.remote.id) {
          return {
            ...feature.data,
            ...this.remoteConfig
          }
        }

        return {
          ...feature.data,
          ...feature.properties
        }
      })
    },
    drawMap () {
      d3.select(`#${this.uid} .svg`).selectAll('*').remove()

      this.max = d3.max(this.values, (d) => d.total)
      this.median = d3.median(this.values, (d) => d.total)
      this.min = d3.min(this.values, (d) => d.total)

      const wrapper = this.$refs['map-container']
        .querySelector(`#${this.uid} .map-wrapper`)
        .getBoundingClientRect()

      const width = wrapper.width

      const height =
        this.windowWidth > 700
          ? this.config.mapHeightDescktop
          : this.config.mapHeightMobile

      const mb = this.windowWidth > 700 ? '76px' : '182px'
      const color = d3
        .scaleLinear()
        .domain([this.min, this.median, this.max])
        .range(this.properties.config.colors.range)

      this.tooltip = d3.select(`#${this.uid} .tooltip`).style('opacity', 0)

      const projection = d3
        .geoIdentity()
        .reflectY(this.flipMapY)
        .reflectX(this.flipMapX)
        .fitSize([width, height], this.geo)

      const path = d3.geoPath(projection)

      const svg = d3.select(`#${this.uid} .svg`)

      if (this.remoteButton) {
        this.remoteButton.style.backgroundColor = color(this.remote.data.total)
      }

      svg
        .attr('viewBox', [0, 0, width, height])
        .attr('height', height)
        .style('margin-bottom', mb)
        .append('g')
        .classed('map-layer', true)
        .selectAll(`#${this.uid} .svg path`)
        .data(this.features)
        .enter()
        .append('path')
        .attr('id', (d) => `map-${d.properties.id}`)
        .attr('d', path)
        .attr('stroke', this.windowWidth > 700 ? this.colors.text.top : 'none')
        .style('cursor', 'pointer')
        .style('fill', (d) => color(d.data.total))
        .on('mouseover', (d) => {
          this.selected = this.selected === d.properties.id ? null : d
          this.tooltip.transition().duration(150).style('opacity', 1)
          outline.attr('d', path(d))
        })
        .on('mouseout', (d) => {
          this.tooltip.transition().duration(150).style('opacity', 0)
          outline.attr('d', null)
        })
        .on('mousemove', (d) => {
          this.tooltip
            .style('top', d3.event.clientY + 'px')
            .style('left', d3.event.clientX + 'px')
        })
        .on('click', (d) => {
          const node = svg.node()
          this.selected = this.selected === d.properties.id ? null : d
          node.dispatchEvent(new CustomEvent('input'))

          const item = this.features.find(
            (feature) => feature.properties.id === d.properties.id
          )

          this.itemSelected =
            item.properties.id === this.remote.id
              ? { ...item.data, ...this.remoteConfig }
              : {
                ...item.data,
                ...item.properties
              }

          this.total = this.itemSelected.total

          const keys = new Array(
            omit(['details', 'id', 'name', 'total', 'text'], this.itemSelected)
          )
          this.sortBarsData(keys)
          this.$refs.select && this.$refs.select.close()
          this.$refs.ranking && this.$refs.ranking.close()

          if (this.windowWidth < 700) {
            const element = document.getElementById(`${this.uid}`)
            element.scrollIntoView({ behavior: 'smooth' })
          }

          const d3node = `#${this.uid} .svg`

          if (d.properties.id !== this.remote.id) {
            d3.selectAll(`${d3node} path:not(#map-${d.properties.id})`)
              .transition()
              .duration(250)
              .ease(d3.easeLinear)
              .style('opacity', 0.2)
              .attr('stroke-width', 0)
              .attr('stroke', 'none')

            d3.selectAll(`${d3node} path#map-${d.properties.id}`)
              .transition()
              .duration(250)
              .ease(d3.easeLinear)
              .style('opacity', 1)
              .attr('stroke-width', '1px')
              .attr('stroke', 'rgba(255, 255, 255, 0.5)')

            if (this.remoteButton) {
              this.remoteButton.style.opacity = '0.2'
            }
          }

          if (d.properties.id === this.remote.id) {
            d3.selectAll(`${d3node} path:not(#map-${d.properties.id})`)
              .transition()
              .duration(250)
              .ease(d3.easeLinear)
              .style('opacity', 0.2)
              .attr('stroke-width', 0)
              .attr('stroke', 'none')

            if (this.remoteButton) {
              this.remoteButton.style.opacity = '1'
            }
          }

          if (!this.idbutton) {
            this.idbutton = this.config.mapActiveTag
          }

          this.$gtm.trackEvent({
            event: 'event_graph', // Event type [default = 'interaction'] (Optional)
            category: 'map:' + this.idbutton,
            action: 'select region',
            label: item.data.id,
            value: 1,
            noninteraction: false // Optional
          })
        })

      const outline = svg
        .append('path')
        .attr('class', 'line')
        .attr('fill', 'none')
        .attr('stroke-width', this.windowWidth > 700 ? '2px' : 0)
        .attr('stroke', this.windowWidth > 700 ? this.colors.text.top : 'none')
        .attr('pointer-events', 'none')
    },
    setTotalValues (data) {
      return data.map((item) => {
        const total = Object.keys(item)
          .filter((key) => key !== 'id')
          .reduce((acc, val) => acc + item[val], 0)

        return {
          ...item,
          id: item.id,
          total: total
        }
      })
    },
    sortBarsData (data) {
      const unique = [
        ...new Set(data.map((obj) => Object.keys(obj)).flat())
      ].filter((k) => k !== 'id' && k !== 'total')
      const formatted = unique
        .map((key) => {
          let total = 0
          data.map((item) =>
            Object.keys(item).map((k) => {
              if (k === key) {
                total += item[key]
              }
            })
          )

          return {
            name: key,
            value: total
          }
        })
        .sort((a, b) => b.value - a.value)

      this.barsData = formatted
    },
    toggleTags (button) {
      this.selectedColor = this.colors[button.id]
      this.currentData = this.data[button.id]
      this.idbutton = button.id

      this.$gtm.trackEvent({
        event: 'event_graph', // Event type [default = 'interaction'] (Optional)
        category: 'map',
        action: 'select tag',
        label: button.id,
        value: 1,
        noninteraction: false // Optional
      })

      this.init()
      this.drawMap()

      if (this.itemSelected) {
        this.setSelection(this.itemSelected.id)
      }
    },
    selectTags (tag) {
      this.selectedColor = this.colors[tag]
      this.currentData = this.data[tag]

      this.$gtm.trackEvent({
        event: 'event_graph', // Event type [default = 'interaction'] (Optional)
        category: 'map',
        action: 'select tag',
        label: tag,
        value: 1,
        noninteraction: false // Optional
      })

      this.selectedTag = this.tagButtons.find((t) => t.id === tag)

      this.$refs.selectemployment.close()

      this.init()

      if (this.itemSelected) {
        this.setSelection(this.itemSelected.id)
      }
    },
    setSelection (id) {
      this.setAll()
      const pathElement = this.$refs['map-container']
        .querySelector(`#${this.uid} #map-${id}`)
      pathElement.dispatchEvent(new Event('click'))
    },
    setAll () {
      this.itemSelected = null
      this.setSelectedData()
      d3.selectAll(`#${this.uid} .map-wrapper .svg path`)
        .transition()
        .duration(250)
        .ease(d3.easeLinear)
        .attr('stroke', this.windowWidth > 700 ? this.colors.text.top : 'none')
        .attr('stroke-width', this.windowWidth > 700 ? '1px' : 0)
        .style('opacity', 1)

      if (this.remoteButton) {
        this.remoteButton.style.opacity = '1'
      }

      this.sortBarsData(this.values)
      this.total = d3.sum(this.values, (d) => d.total)
      this.$refs.select && this.$refs.select.close()
      this.$refs.ranking && this.$refs.ranking.close()
    },
    resize () {
      this.windowWidth = window.innerWidth
    },
    scroll () {
      this.tooltip.style('opacity', 0)
    }

  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resize)
    window.removeEventListener('scroll', this.scroll)
  }
}
